import React, { useState } from "react";
import { api, waLink, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_ADDRESS } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "../lib/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Message received. We will write back within 24 hours.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <Seo
        title="Contact YourAstroHub — Book a Consultation | Bhupaal Bisht"
        description="Contact YourAstroHub for Vedic astrology consultations, courses, or general enquiries. WhatsApp +91 92892 12630 or email us — response within 24 hours."
        keywords="Contact Vedic Astrologer, YourAstroHub Contact, Bhupaal Bisht Contact, WhatsApp Astrologer"
        path="/contact"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
      />
      <section className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="max-w-3xl">
            <div className="mb-6">
              <img
                src="/assets/logo-full.png"
                alt="YourAstroHub - Vedic Astrology Consultation and Education Logo"
                className="h-24 md:h-32 w-auto drop-shadow-[0_0_20px_rgba(212,175,55,0.2)]"
                width="240" height="240"
                loading="lazy"
              />
            </div>
            <div className="eyebrow mb-4">Get in Touch</div>
            <h1 className="font-serif text-5xl md:text-6xl font-light leading-[1.05]">
              We&apos;d love to <span className="gold-text italic">hear from you.</span>
            </h1>
            <p className="text-foreground/70 mt-5 text-lg">For consultations, courses or collaborations — send us a note and we will respond within one business day.</p>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-7 rounded-3xl border border-foreground/10 p-8 space-y-5">
            <h3 className="font-serif text-2xl">Send us a message</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label htmlFor="c-name">Name</Label><Input id="c-name" data-testid="contact-name" required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="rounded-full mt-2" /></div>
              <div><Label htmlFor="c-email">Email</Label><Input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="rounded-full mt-2" /></div>
              <div><Label htmlFor="c-phone">Phone</Label><Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="rounded-full mt-2" /></div>
              <div><Label htmlFor="c-subject">Subject</Label><Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={(e)=>setForm({...form, subject: e.target.value})} className="rounded-full mt-2" /></div>
            </div>
            <div><Label htmlFor="c-message">Message</Label><Textarea id="c-message" data-testid="contact-message" rows={5} required value={form.message} onChange={(e)=>setForm({...form, message: e.target.value})} className="rounded-2xl mt-2" /></div>
            <Button type="submit" data-testid="contact-submit" disabled={busy} size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
              {busy ? "Sending..." : sent ? "Sent" : "Send Message"}
            </Button>
          </form>

          <aside className="lg:col-span-5 space-y-4">
            <div className="rounded-3xl border border-foreground/10 p-6">
              <div className="eyebrow mb-4">Direct Channels</div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-1 text-[#D4AF37]" /><div><div className="font-medium">Email</div><a href={`mailto:${CONTACT_EMAIL}`} className="text-foreground/70 hover:text-[#D4AF37]">{CONTACT_EMAIL}</a></div></li>
                <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-1 text-[#D4AF37]" /><div><div className="font-medium">Phone</div><a href={`tel:${CONTACT_PHONE}`} className="text-foreground/70 hover:text-[#D4AF37]">{CONTACT_PHONE}</a></div></li>
                <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-1 text-[#D4AF37]" /><div><div className="font-medium">Address</div><span className="text-foreground/70">{CONTACT_ADDRESS}</span></div></li>
                <li>
                  <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp">
                    <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#20b957] text-white mt-2"><MessageCircle className="w-4 h-4 mr-2" />Chat on WhatsApp</Button>
                  </a>
                </li>
              </ul>
            </div>

            <div className="rounded-3xl overflow-hidden border border-foreground/10 aspect-[4/3]">
              <iframe
                title="location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224345.83942071408!2d76.81306999726561!3d28.647279900000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd5b347eb62d%3A0x37205b715389640!2sDelhi!5e0!3m2!1sen!2sin!4v1700000000000"
                width="100%" height="100%"
                loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
